import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import { auth } from '../firebaseConfig';
import FullScreenButton from './FullScreenButton';
import {
  TWO_COL_ITEMS_PER_ROW as ITEMS_PER_ROW,
  TWO_COL_SLOT_COLS as SLOT_COLS,
  displayItemNumber,
  expandItemsForNewVisualRow,
  gridToSequentialItems,
  isNumberCol,
  itemsToTwoColumnGrid,
  memoColInSlot as memoCol,
  remapIdsColumnMajor,
  sequentialIdxFromVisual,
  slotFromCol,
  valueColInSlot as itemNameCol,
} from '../utils/twoColumnGridLayout';

const CUSTOMER_ID = 'otoki';
const MIN_ROWS = 8;
const GRID_COL_COUNT = ITEMS_PER_ROW * SLOT_COLS;
const HEADERS = Array.from({ length: ITEMS_PER_ROW }, () => ['No.', '품목명', '비고']).flat();

type ItemRow = { id: string | null; item_name: string; memo: string };

function emptyItemRow(): ItemRow {
  return { id: null, item_name: '', memo: '' };
}

function itemNumberRenderer(
  this: unknown,
  instance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties,
) {
  Handsontable.renderers.TextRenderer.call(
    this,
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  );
  const slot = Math.floor(col / SLOT_COLS);
  const visualRowCount = instance.countRows();
  td.textContent = String(displayItemNumber(row, slot, visualRowCount));
  td.style.textAlign = 'center';
  td.style.color = '#546e7a';
  td.style.fontWeight = '600';
  td.style.background = '#f5f7f9';
}

function padItemSlots(items: ItemRow[], minCount = MIN_ROWS): ItemRow[] {
  const next = [...items];
  while (next.length < minCount) {
    next.push(emptyItemRow());
  }
  if (next.length % ITEMS_PER_ROW !== 0) {
    next.push(emptyItemRow());
  }
  return next;
}

function itemsToGridData(items: ItemRow[]): string[][] {
  return itemsToTwoColumnGrid(
    items,
    MIN_ROWS,
    emptyItemRow,
    item => [item.item_name, item.memo],
  );
}

function itemIdsFromItems(items: ItemRow[]): (string | null)[] {
  return padItemSlots(items).map(item => item.id);
}

function flattenGridToItems(data: string[][], ids: (string | null)[]): ItemRow[] {
  return gridToSequentialItems(data, ids, (id, item_name, memo) => ({
    id,
    item_name,
    memo,
  }));
}

interface Props {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
  embedded?: boolean;
}

const ProcurementItemPage: React.FC<Props> = ({ isFullScreen, setIsFullScreen, embedded = false }) => {
  const hotRef = useRef<any>(null);
  const itemIdsRef = useRef<(string | null)[]>([]);
  const deletedIdsRef = useRef<Set<string>>(new Set());
  const searchMatchCursorRef = useRef(0);
  const lastSearchQueryRef = useRef('');
  const [tableData, setTableData] = useState<any[][]>([]);
  const [itemIds, setItemIds] = useState<(string | null)[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchStatus, setSearchStatus] = useState<{ type: 'found' | 'notfound' | 'warn'; text: string } | null>(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    deletedIdsRef.current.clear();
    try {
      const res = await fetch(`/api/procurement/items?customerId=${CUSTOMER_ID}`);
      if (!res.ok) throw new Error('품목 목록을 불러오지 못했습니다.');
      const data = await res.json();
      const items: ItemRow[] = data.map((r: { id: string; item_name: string; memo?: string | null }) => ({
        id: r.id,
        item_name: r.item_name,
        memo: r.memo || '',
      }));
      setItemIds(itemIdsFromItems(items));
      setTableData(itemsToGridData(items));
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    itemIdsRef.current = itemIds;
  }, [itemIds]);

  const syncTableData = () => {
    setTableData(hotRef.current?.hotInstance?.getData() || []);
  };

  const handleSave = async () => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const data: string[][] = hot.getData();

    const deleteIds = new Set<string>(deletedIdsRef.current);
    const upsertRows: Array<{ id?: string; item_name: string; memo: string | null }> = [];
    const items = flattenGridToItems(data, itemIds);

    items.forEach(item => {
      const id = item.id || undefined;
      const itemName = item.item_name.trim();
      const memo = item.memo.trim() || null;

      if (id && !itemName) {
        deleteIds.add(id);
        return;
      }
      if (!itemName) return;

      upsertRows.push({ id, item_name: itemName, memo });
    });

    if (upsertRows.length === 0 && deleteIds.size === 0) {
      alert('저장할 변경사항이 없습니다.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/procurement/items/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: CUSTOMER_ID,
          createdBy: auth.currentUser?.email || null,
          rows: upsertRows,
          deleteIds: [...deleteIds],
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || '저장 실패');
      deletedIdsRef.current.clear();
      setMessage('저장되었습니다.');
      await loadItems();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRow = () => {
    const hot = hotRef.current?.hotInstance;
    const data: string[][] = hot ? hot.getData() : tableData;
    const oldVisualRowCount = data.length;
    const items = flattenGridToItems(data, itemIdsRef.current);
    const expanded = expandItemsForNewVisualRow(items, oldVisualRowCount, emptyItemRow);
    const newIds = expanded.map(item => item.id);
    const newGrid = itemsToGridData(expanded);
    itemIdsRef.current = newIds;
    setItemIds(newIds);
    setTableData(newGrid);
    hot?.loadData(newGrid);
    hot?.render();
  };

  const handleRemoveItemSlot = useCallback((visualRow: number, col: number) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    const slot = slotFromCol(col);
    const data: string[][] = hot.getData();
    const visualRowCount = data.length;
    const items = flattenGridToItems(data, itemIdsRef.current);
    const seqIdx = sequentialIdxFromVisual(visualRow, slot, visualRowCount);
    const removed = items[seqIdx];

    if (!removed?.item_name && !removed?.id) return;

    if (removed.id) deletedIdsRef.current.add(removed.id);
    items.splice(seqIdx, 1);

    const newGrid = itemsToGridData(items);
    const newIds = itemIdsFromItems(items);
    itemIdsRef.current = newIds;
    setItemIds(newIds);
    setTableData(newGrid);
    hot.loadData(newGrid);
    hot.render();
  }, []);

  const handleCreateRow = (index: number, amount: number) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const newData: string[][] = hot.getData();
    const newIds = remapIdsColumnMajor(tableData, itemIdsRef.current, newData);
    itemIdsRef.current = newIds;
    setItemIds(newIds);
    setTableData(newData);
    hot.render();
  };

  const handleItemSearch = () => {
    const query = searchText.trim();
    if (!query) {
      setSearchStatus({ type: 'warn', text: '검색어를 입력하세요.' });
      return;
    }

    const hot = hotRef.current?.hotInstance;
    const data: string[][] = hot ? hot.getData() : tableData;
    const matches: Array<{ visualRow: number; col: number; logicalIdx: number; itemName: string }> = [];

    data.forEach((row, visualRow) => {
      for (let slot = 0; slot < ITEMS_PER_ROW; slot++) {
        const itemName = String(row[itemNameCol(slot)] || '').trim();
        if (itemName && itemName.includes(query)) {
          matches.push({
            visualRow,
            col: itemNameCol(slot),
            logicalIdx: visualRow * ITEMS_PER_ROW + slot,
            itemName,
          });
        }
      }
    });

    if (matches.length === 0) {
      searchMatchCursorRef.current = 0;
      lastSearchQueryRef.current = query;
      setSearchStatus({ type: 'notfound', text: `"${query}"(와)과 일치하는 등록 품목이 없습니다.` });
      return;
    }

    if (lastSearchQueryRef.current !== query) {
      searchMatchCursorRef.current = 0;
      lastSearchQueryRef.current = query;
    } else {
      searchMatchCursorRef.current = (searchMatchCursorRef.current + 1) % matches.length;
    }

    const match = matches[searchMatchCursorRef.current];
    const matchSlot = slotFromCol(match.col);

    if (hot) {
      hot.selectCell(match.visualRow, match.col);
      hot.scrollViewportTo(match.visualRow, match.col);
    }

    const positionLabel = matches.length > 1
      ? `${searchMatchCursorRef.current + 1}/${matches.length} · `
      : '';
    setSearchStatus({
      type: 'found',
      text: `${positionLabel}${displayItemNumber(match.visualRow, matchSlot, data.length)}번째 "${match.itemName}" — 등록된 품목입니다.`,
    });
  };

  const searchStatusColor =
    searchStatus?.type === 'found'
      ? '#2e7d32'
      : searchStatus?.type === 'notfound'
        ? '#c62828'
        : '#f57c00';

  return (
    <div style={{
      padding: embedded ? 0 : '12px 16px',
      height: embedded ? '100%' : 'calc(100vh - 48px)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: '#1976d2', fontSize: 22 }}>조달실 — 품목 관리</h2>
          <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
        </div>
      )}
      <p style={{ color: '#666', fontSize: 14, margin: '0 0 12px' }}>
        OLS 출고 송장에 사용할 품목을 등록합니다. 품목명을 Delete 키로 지우거나 셀에서 우클릭 → 품목 삭제 후 저장하면 삭제됩니다. (기존 송장에 포함된 품목 행은 함께 제거됩니다.)
      </p>
      <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={handleAddRow} style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>+ 행 추가</button>
        <button type="button" onClick={handleSave} disabled={saving || loading} style={{ padding: '6px 16px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{saving ? '저장 중…' : '저장'}</button>
        <button type="button" onClick={loadItems} style={{ padding: '6px 16px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>새로고침</button>
        <span style={{ width: 1, height: 24, background: '#ddd', margin: '0 4px' }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: '#444' }}>
          품목 검색
          <input
            type="text"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              if (searchStatus) setSearchStatus(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleItemSearch();
              }
            }}
            placeholder="품목명 일부 입력 (예: 표고)"
            style={{
              width: 200,
              padding: '6px 10px',
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14,
            }}
          />
        </label>
        <button
          type="button"
          onClick={handleItemSearch}
          disabled={loading}
          style={{ padding: '6px 16px', background: '#455a64', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
        >
          검색
        </button>
      </div>
      {searchStatus && (
        <div style={{ color: searchStatusColor, marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
          {searchStatus.text}
        </div>
      )}
      {message && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{message}</div>}
      {loading ? (
        <div style={{ padding: 24, color: '#888' }}>불러오는 중…</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <style>{`
            .proc-item-page .htCore td.proc-item-no {
              background: #f5f7f9 !important;
              color: #546e7a;
              font-weight: 600;
              text-align: center;
            }
            .proc-item-page .htCore th.proc-item-no {
              background: #eceff1 !important;
              text-align: center;
            }
            .proc-item-page .htCore td.proc-item-slot-end {
              border-right: 2px solid #cfd8dc !important;
            }
            .proc-item-page .htCore th.proc-item-slot-end {
              border-right: 2px solid #b0bec5 !important;
            }
          `}</style>
          <HotTable
            ref={hotRef}
            className="proc-item-page"
            data={tableData}
            colHeaders={HEADERS}
            rowHeaders={false}
            width="100%"
            height="100%"
            licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
            stretchH="all"
            colWidths={[44, undefined, undefined, 44, undefined, undefined]}
            contextMenu={{
              items: {
                remove_item: {
                  name: '품목 삭제',
                  callback(this: Handsontable) {
                    const sel = this.getSelectedLast();
                    if (!sel) return;
                    const [row, col] = sel;
                    handleRemoveItemSlot(row, col);
                  },
                  disabled(this: Handsontable) {
                    const sel = this.getSelectedLast();
                    if (!sel) return true;
                    const [row, col] = sel;
                    if (isNumberCol(col)) return true;
                    const slot = slotFromCol(col);
                    const itemName = String(this.getDataAtCell(row, itemNameCol(slot)) || '').trim();
                    const seqIdx = sequentialIdxFromVisual(row, slot, this.countRows());
                    const id = itemIdsRef.current[seqIdx];
                    return !itemName && !id;
                  },
                },
              },
            }}
            beforeRemoveRow={() => false}
            afterChange={(changes, source) => {
              if (source === 'loadData' || !changes) return;
              const hot = hotRef.current?.hotInstance;
              if (hot) {
                for (const [row, col] of changes) {
                  if (typeof col === 'number' && isNumberCol(col)) {
                    hot.setDataAtCell(row as number, col, '', 'loadData');
                  }
                }
              }
              syncTableData();
            }}
            afterCreateRow={(index, amount) => {
              handleCreateRow(index, amount);
              hotRef.current?.hotInstance?.render();
            }}
            afterGetColHeader={(col, TH) => {
              if (isNumberCol(col)) {
                TH.classList.add('proc-item-no');
              } else if (col === memoCol(0)) {
                TH.classList.add('proc-item-slot-end');
              }
            }}
            cells={(row, col) => {
              const props: Handsontable.CellMeta = {};
              if (isNumberCol(col)) {
                props.readOnly = true;
                props.className = 'proc-item-no';
                props.renderer = itemNumberRenderer;
              } else if (col === memoCol(0)) {
                props.className = 'proc-item-slot-end';
              }
              return props;
            }}
            columns={Array.from({ length: GRID_COL_COUNT }, (_, col) => (
              isNumberCol(col)
                ? { type: 'text' as const, readOnly: true, renderer: itemNumberRenderer }
                : { type: 'text' as const }
            ))}
          />
        </div>
      )}
    </div>
  );
};

export default ProcurementItemPage;
